import { useCallback, useSyncExternalStore } from "react";
import { useDataStore } from "../contexts";
import type { DataSelector, StrictTransferData, TransferState } from "../store";

/**
 * Hook for selector-based data subscriptions.
 * Only re-renders when selected value changes.
 *
 * @param selector - Function to select data from state
 * @returns Selected value
 */
export function useDataSelector<T>(selector: DataSelector<T>): T {
	const store = useDataStore();

	const subscribe = useCallback((cb: () => void) => store.subscribe(selector, cb), [store, selector]);

	const getSnapshot = useCallback(() => store.getSnapshot(selector), [store, selector]);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook for transfer state by ID.
 *
 * When called with an explicit type parameter the returned `data` / `originalData`
 * fields are narrowed to `StrictTransferData<T> | null`.
 *
 * @typeParam T - Optional strict transfer shape
 * @param transferId - Transfer identifier
 * @returns TransferState (optionally narrowed) or undefined
 */
export function useTransferState<T extends Record<string, unknown> = Record<string, unknown>>(
	transferId: string
): (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null }) | undefined {
	const selector = useCallback(
		(state: { transfers: Map<string, TransferState> }) => state.transfers.get(transferId),
		[transferId]
	);

	return useDataSelector(selector) as
		| (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null })
		| undefined;
}

/**
 * Hook for transfer field value.
 *
 * When called with an explicit type parameter `T` and field `K`, the return type
 * is narrowed to `T[K] | undefined`.
 *
 * @typeParam T - Optional strict transfer shape
 * @typeParam K - Field key (restricted to `keyof T` when strict)
 * @param transferId - Transfer identifier
 * @param field - Field name
 * @returns Field value (typed to `T[K]` when strict)
 */
export function useTransferField<
	T extends Record<string, unknown> = Record<string, unknown>,
	K extends string & keyof T = string & keyof T,
>(transferId: string, field: K): T[K] | undefined {
	const selector = useCallback(
		(state: { transfers: Map<string, TransferState> }) => state.transfers.get(transferId)?.data?.[field],
		[transferId, field]
	);

	return useDataSelector(selector) as T[K] | undefined;
}

/**
 * Hook for transfer loading state.
 *
 * @param transferId - Transfer identifier
 * @returns Loading state
 */
export function useTransferLoading(transferId: string): boolean {
	const selector = useCallback(
		(state: { transfers: Map<string, TransferState> }) => state.transfers.get(transferId)?.isLoading ?? false,
		[transferId]
	);

	return useDataSelector(selector);
}

/**
 * Hook for transfer dirty state.
 *
 * @param transferId - Transfer identifier
 * @returns Dirty state
 */
export function useTransferDirty(transferId: string): boolean {
	const selector = useCallback(
		(state: { transfers: Map<string, TransferState> }) => state.transfers.get(transferId)?.isDirty ?? false,
		[transferId]
	);

	return useDataSelector(selector);
}
